import React, { useState, useEffect, ChangeEvent } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useTrackCreation } from "../../../../context/TrackCreationContext";
import FormGroup from "../../../../components/common/FormGroup";
import TextInput from "../../../../components/ui/TextInput";
import Button from "../../../../components/ui/Button";
import { FileInput } from "../../../../components/common/FileInput";
import ImageSpecsBadges from "../../../../components/common/ImageSpecsBadges";
import { useImageValidation } from "../../../../hooks/useImageValidation";
import Dropdown from "../../../../components/common/Dropdown";
import NumberInput from "../../../../components/common/NumberInput";
import {
    createBadge,
    getProgressionConfig,
    getScanConfig,
    getScannableObjectTags,
    ScannableObjectTagResponse,
} from "../../../../api/progression";
import { uploadFileToS3 } from "../../../../api/s3";
import { IMAGE_MAX_SIZE_200_KB } from "../../../../utils/image";
import i18n from "../../../../i18n";
import { compoundPluralKey } from "../../../../utils/pluralKey";
import styles from "./CreateBadge.module.css";
import stepStyles from "../createStep.module.css";
import pageHeader from "../../../../components/layouts/PageHeader.module.css";

interface CreateBadgeProps {
    navigateToNext: () => void;
}

const CreateBadge: React.FC<CreateBadgeProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const token = localStorage.getItem("token") || "";

    const linkId = trackData?.trackId || trackData?.id;

    const [name, setName] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [imageUrl, setImageUrl] = useState<string>("");
    const [progressionConfigId, setProgressionConfigId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [uploadingImage, setUploadingImage] = useState<boolean>(false);

    // Condition fields
    const [selectedTagId, setSelectedTagId] = useState<string>("");
    const [objectsRequired, setObjectsRequired] = useState<number>(1);
    const [storiesPerObject, setStoriesPerObject] = useState<number>(1);

    const [tags, setTags] = useState<ScannableObjectTagResponse[]>([]);
    const imageValidation = useImageValidation({
        type: "square",
        imageUrl: imageUrl || undefined,
    });

    useEffect(() => {
        const loadData = async () => {
            if (!trackData.trackId) return;

            try {
                const [configResponse, scanConfigResponse] = await Promise.all([
                    getProgressionConfig({ token, trackId: trackData.trackId }),
                    getScanConfig({ token, trackId: trackData.trackId }),
                ]);

                if (configResponse?.progressionConfig) {
                    setProgressionConfigId(configResponse.progressionConfig.id);
                }

                if (scanConfigResponse?.scanConfig) {
                    const scanConfigId = scanConfigResponse.scanConfig.id;
                    const tagsResponse = await getScannableObjectTags({
                        token,
                        scanConfigId,
                    });

                    if (tagsResponse?.scannableObjectTags) {
                        setTags(tagsResponse.scannableObjectTags);
                    }
                }
            } catch (error) {
                console.error("Error loading data:", error);
            }
        };

        loadData();
    }, [trackData.trackId, token]);

    const handleImageUpload = async (files: File[] | File) => {
        const file = Array.isArray(files) ? files[0] : files;
        if (!file) return;

        setUploadingImage(true);
        await imageValidation.validateImage(file);
        try {
            const response = await uploadFileToS3({ token, file });
            if (response?.url) {
                setImageUrl(response.url);
            }
        } catch (error) {
            console.error("Error uploading image:", error);
        } finally {
            setUploadingImage(false);
        }
    };

    const handleSave = async () => {
        if (!progressionConfigId || !name || !imageUrl || !text) return;

        setLoading(true);
        try {
            // Build conditions - tag is optional (undefined means any object)
            const conditions: Array<{
                conditionType: "TAG_COUNT" | "OBJECT_WAYPOINTS";
                tagId?: string;
                taggedObjectsRequired?: number;
                waypointsPerObjectRequired?: number;
            }> = [
                {
                    conditionType: "TAG_COUNT" as const,
                    tagId: selectedTagId || undefined, // undefined means any object
                    taggedObjectsRequired: objectsRequired,
                    waypointsPerObjectRequired: storiesPerObject,
                },
            ];

            await createBadge({
                token,
                progressionConfigId,
                name,
                text: text || undefined,
                imageUrl: imageUrl || undefined,
                conditions,
                language: trackData.language || "EN",
            });
            navigateToNext();
        } catch (error) {
            console.error("Error creating badge:", error);
        } finally {
            setLoading(false);
        }
    };

    const tagOptions = tags.map((tag) => {
        const langCode = i18n.language?.toUpperCase();
        const localized = tag.scannableObjectTagLocalized?.find((loc) => loc.language?.toUpperCase() === langCode);
        return {
            value: tag.id,
            label: localized?.name || tag.scannableObjectTagLocalized?.[0]?.name || tag.id,
        };
    });

    const selectedTag = tags.find((t) => t.id === selectedTagId);
    const selectedTagName = selectedTag
        ? (() => {
              const langCode = i18n.language?.toUpperCase();
              const localized = selectedTag.scannableObjectTagLocalized?.find(
                  (loc) => loc.language?.toUpperCase() === langCode,
              );
              return localized?.name || selectedTag.scannableObjectTagLocalized?.[0]?.name || selectedTag.id;
          })()
        : "";

    const exampleText = selectedTagName
        ? t(compoundPluralKey("badgeConditionExample", objectsRequired, storiesPerObject), {
              tag: selectedTagName,
              objects: objectsRequired,
              stories: storiesPerObject,
          })
        : t(compoundPluralKey("badgeConditionExampleAnyObject", objectsRequired, storiesPerObject), {
              objects: objectsRequired,
              stories: storiesPerObject,
          });

    return (
        <div className={styles.container}>
            {/* Breadcrumb */}
            <div className={styles.breadcrumb}>
                <Link to={`/create/tour/${linkId}/levels`}>{t("progression")}</Link>
                <span>›</span>
                <Link to={`/create/tour/${linkId}/badges`}>{t("badges")}</Link>
                <span>›</span>
                <span style={{ color: "var(--green-600)" }}>{t("createBadge")}</span>
            </div>

            {/* Header */}
            <div className={pageHeader.withBreadcrumb}>
                <div className={pageHeader.titleSection}>
                    <div className={pageHeader.content}>
                        <h2 className="settings-title">{t("createBadge")}</h2>
                        <p className={pageHeader.subtitle}>{t("createBadgeDescription")}</p>
                    </div>
                </div>
            </div>

            {/* Form */}
            <div className={styles.sectionCard}>
                {/* Badge name */}
                <FormGroup
                    title={t("badgeName")}
                    subtitle={t("badgeNameDescription")}
                    tooltip={t("doNotTranslateTooltip")}
                    required
                >
                    <TextInput
                        placeholder={t("enterBadgeName")}
                        value={name}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setName(e.target.value)}
                        size="medium"
                    />
                </FormGroup>

                {/* Conditions */}
                <div className={styles.conditionsCard}>
                    <h3 className={styles.conditionsTitle}>{t("obtentionConditions")}</h3>
                    <p className={styles.conditionsDescription}>{t("obtentionConditionsDescription")}</p>

                    {/* Tag to discover (optional - leave empty for any object) */}
                    <FormGroup title={t("tagToDiscover")} subtitle={t("tagToDiscoverDescriptionOptional")}>
                        <Dropdown
                            label=""
                            options={tagOptions}
                            value={selectedTagId}
                            onChange={setSelectedTagId}
                            placeholder={t("selectTagOptional")}
                            loading={false}
                            error={false}
                        />
                    </FormGroup>

                    {/* Number of objects to discover */}
                    <FormGroup
                        title={t("numberOfObjectsToDiscover")}
                        subtitle={t("numberOfObjectsToDiscoverForBadge")}
                        required
                    >
                        <NumberInput
                            placeholder={t("enterNumber")}
                            value={objectsRequired}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setObjectsRequired(parseInt(e.target.value) || 1)
                            }
                            min={1}
                        />
                    </FormGroup>

                    {/* Number of stories per object */}
                    <FormGroup
                        title={t("numberOfStoriesPerObject")}
                        subtitle={t("numberOfStoriesPerObjectDescription")}
                        required
                    >
                        <NumberInput
                            placeholder={t("enterNumber")}
                            value={storiesPerObject}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                setStoriesPerObject(parseInt(e.target.value) || 1)
                            }
                            min={1}
                        />
                        {exampleText && <p className={styles.exampleText}>{exampleText}</p>}
                    </FormGroup>
                </div>

                {/* Image */}
                <FormGroup title={t("image")} subtitle={t("badgeImageDescription")} required>
                    <ImageSpecsBadges spec="square" warnings={imageValidation.warnings} />
                    {imageUrl && (
                        <div style={{ marginBottom: "12px" }}>
                            <img
                                src={imageUrl}
                                alt={t("badgeImage")}
                                style={{
                                    maxWidth: "150px",
                                    maxHeight: "150px",
                                    objectFit: "contain",
                                    borderRadius: "8px",
                                }}
                            />
                        </div>
                    )}
                    <FileInput
                        onFileSelected={handleImageUpload}
                        placeholder={uploadingImage ? t("uploading") : t("dragDropOrClick")}
                        disabled={uploadingImage}
                        acceptedFileTypes={{ "image/*": [".png", ".jpg", ".jpeg", ".webp"] }}
                        maxSize={IMAGE_MAX_SIZE_200_KB}
                        maxSizeLabel={t("imageMaxSize200kb")}
                    />
                </FormGroup>

                {/* Text */}
                <FormGroup
                    title={t("text")}
                    subtitle={t("badgeTextDescription")}
                    tooltip={t("doNotTranslateTooltip")}
                    required
                >
                    <TextInput
                        placeholder={t("enterText")}
                        value={text}
                        onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => setText(e.target.value)}
                        size="big"
                        numberOfLines={4}
                        style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: "8px",
                            padding: "12px",
                            width: "100%",
                            boxSizing: "border-box",
                        }}
                    />
                </FormGroup>
            </div>

            {/* Footer buttons — mock .foot */}
            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    label={loading ? t("saving") : t("create")}
                    onClick={handleSave}
                    disabled={!name || !imageUrl || !text || loading || uploadingImage}
                    variant="primary"
                    shape="pill"
                    className={stepStyles.continueBtn}
                />
            </div>
        </div>
    );
};

export default CreateBadge;
